import { IconButton, InputAdornment, type TextFieldProps } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  AT_LEAST_ONE_LOWER_CASE,
  AT_LEAST_ONE_NUMBER,
  AT_LEAST_ONE_SPECIAL_CHARACTER,
  AT_LEAST_ONE_UPPER_CASE,
  AT_LEAST_TEN_CHARACTERS,
  FormTextField,
} from 'src/components/Forms';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { ResendInvitePasswordValidation } from './resend-invite-password-validation';
import {
  PasswordsValidation,
  ResendInviteWithPasswordFormFields,
  ResendInviteWithPasswordFormModel,
} from './types';

const defaultValues: ResendInviteWithPasswordFormModel = {
  [ResendInviteWithPasswordFormFields.Password]: '',
  [ResendInviteWithPasswordFormFields.PasswordConfirm]: '',
};

interface PasswordVisibility {
  isVisible: boolean;
  toggle: () => void;
}

export const ResentInviteWithPasswordModal = () => {
  const { closeModal } = useModalMethods(Modals.ResentInviteWithPassword);
  const { isOpened, modalProps } = useModalState(Modals.ResentInviteWithPassword);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const formMethods = useForm<ResendInviteWithPasswordFormModel>({
    mode: 'all',
    defaultValues,
  });
  const { reset, control, handleSubmit } = formMethods;

  const [password, confirmPassword] = useWatch({
    control,
    name: [
      ResendInviteWithPasswordFormFields.Password,
      ResendInviteWithPasswordFormFields.PasswordConfirm,
    ],
  });
  const passwordsValidationResults = useMemo(
    () => getPasswordsValidation(password, confirmPassword),
    [password, confirmPassword],
  );
  const isFormValid =
    passwordsValidationResults !== null && passwordFieldIsValid(passwordsValidationResults);

  const closeModalAndResetForm = useCallback(() => {
    reset();
    closeModal();
  }, [reset, closeModal]);

  const onFormSubmit = useCallback(
    (formValues: ResendInviteWithPasswordFormModel) => {
      modalProps?.onSubmit(
        modalProps.userId,
        formValues[ResendInviteWithPasswordFormFields.Password],
      );
      closeModalAndResetForm();
    },
    [modalProps, closeModalAndResetForm],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Create a temporary password and send it to the user."
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'resend-invite-cancel' },
        {
          label: 'Resend invite',
          kind: 'primary',
          onClick: handleSubmit(onFormSubmit),
          disabled: !isFormValid,
          testId: 'resend-invite-submit',
        },
      ]}
      onClose={closeModalAndResetForm}
      data-testid="resend-invite-with-password-modal"
    >
      <GenericForm<ResendInviteWithPasswordFormModel> formMethods={formMethods}>
        <FormTextField
          type="textfield"
          name={ResendInviteWithPasswordFormFields.Password}
          label="Password"
          textFieldProps={getPasswordFieldProps('resend-invite-password-input', {
            isVisible: showPassword,
            toggle: () => setShowPassword((show) => !show),
          })}
        />
        <FormTextField
          type="textfield"
          name={ResendInviteWithPasswordFormFields.PasswordConfirm}
          label="Confirm"
          textFieldProps={getPasswordFieldProps('resend-invite-password-confirm-input', {
            isVisible: showConfirmPassword,
            toggle: () => setShowConfirmPassword((show) => !show),
          })}
        />
        <ResendInvitePasswordValidation validationResults={passwordsValidationResults} />
      </GenericForm>
    </ModalContainer>
  );
};

function getPasswordFieldProps(
  testId: string,
  { isVisible, toggle }: PasswordVisibility,
): Partial<TextFieldProps> {
  return {
    type: isVisible ? 'text' : 'password',
    inputProps: { 'data-testid': testId },
    InputProps: {
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            aria-label={isVisible ? 'hide the password' : 'display the password'}
            onClick={toggle}
            edge="end"
          >
            <DynamicIcon library="lucide" name={isVisible ? 'eye-off' : 'eye'} />
          </IconButton>
        </InputAdornment>
      ),
    },
  };
}

function getPasswordsValidation(
  password: string,
  confirmPassword: string,
): PasswordsValidation | null {
  if (!password && !confirmPassword) {
    return null;
  }

  return {
    ...validatePasswordField(password),
    passwordsEqual: password === confirmPassword,
  };
}

function validatePasswordField(password: string): PasswordsValidation {
  return {
    atLeastTenCharacters: AT_LEAST_TEN_CHARACTERS.test(password),
    atLeastOneNumber: AT_LEAST_ONE_NUMBER.test(password),
    atLeastOneLowerCaseLetter: AT_LEAST_ONE_LOWER_CASE.test(password),
    atLeastOneUpperCaseLetter: AT_LEAST_ONE_UPPER_CASE.test(password),
    atLeastOneSpecialCharacter: AT_LEAST_ONE_SPECIAL_CHARACTER.test(password),
  };
}

function passwordFieldIsValid(data: PasswordsValidation): boolean {
  return Object.keys(data).every((key) => data[key as keyof PasswordsValidation]);
}
